import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  AlertTitle
} from '@mui/material';

// Components for this page will be imported here
// import DataPrepFlowDiagram from '../../components/dashboard/DataPrepFlowDiagram';
// import DataQualityMetrics from '../../components/dashboard/DataQualityMetrics';
// import ClassificationCompleteness from '../../components/dashboard/ClassificationCompleteness';
// import StandardizationStatus from '../../components/dashboard/StandardizationStatus';

const DataPrepPage: React.FC = () => {
  // Mock data for demonstration
  const [dataSourceCoverage, setDataSourceCoverage] = useState({
    complete: false,
    coveragePercent: 85,
    missingSources: ['SAP North America', 'Oracle ERP EMEA']
  });

  const [dataQualityMetrics, setDataQualityMetrics] = useState({
    duplicatesRemoved: 1250,
    duplicateThreshold: 1000,
    missingFieldsFilled: 3450,
    inconsistentNamesStandardized: 780
  });

  const [classificationStats, setClassificationStats] = useState({
    classifiedPercentage: 92.5,
    threshold: 95
  });

  const [standardizationStatus, setStandardizationStatus] = useState({
    currencyStandardized: true,
    unitMeasureStandardized: false,
    anomaliesFound: 145
  });

  // Business Rules Implementation
  const sourcesCoverageAlert = !dataSourceCoverage.complete && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Incomplete Data Source Coverage</AlertTitle>
      Only {dataSourceCoverage.coveragePercent}% of known spend sources are present. 
      Missing sources: {dataSourceCoverage.missingSources.join(', ')}
    </Alert>
  );

  const duplicatesAlert = dataQualityMetrics.duplicatesRemoved > dataQualityMetrics.duplicateThreshold && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>High Number of Duplicates Removed</AlertTitle>
      {dataQualityMetrics.duplicatesRemoved} duplicate entries were removed, 
      exceeding the threshold of {dataQualityMetrics.duplicateThreshold}. 
      This indicates potential issues with data collection processes.
    </Alert>
  );

  const classificationAlert = classificationStats.classifiedPercentage < classificationStats.threshold && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Classification Below Threshold</AlertTitle>
      Current classification rate is {classificationStats.classifiedPercentage}%, 
      which is below the target threshold of {classificationStats.threshold}%. 
      Further cleansing recommended.
    </Alert>
  );

  const standardizationAlert = !standardizationStatus.unitMeasureStandardized && (
    <Alert severity="warning" sx={{ mt: 2 }}>
      <AlertTitle>Standardization Issues</AlertTitle>
      Unit of measure standardization is incomplete. 
      {standardizationStatus.anomaliesFound} anomalies persist. 
      Additional cleaning recommended.
    </Alert>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Data Prep & Cleansing Overview
      </Typography>

      <Grid container spacing={3}>
        {/* Flow Diagram */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              Data Preparation Flow Diagram
              {/* <DataPrepFlowDiagram /> will be placed here */}
            </Typography>
          </Paper>
        </Grid>

        {/* Data Source Coverage */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Source Coverage
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1">
                  Coverage: {dataSourceCoverage.coveragePercent}% complete
                </Typography>
              </Box>
              {sourcesCoverageAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Data Quality Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Quality Metrics
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1">
                  {dataQualityMetrics.duplicatesRemoved} duplicates removed
                </Typography>
              </Box>
              {duplicatesAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Classification Completeness */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Classification Completeness
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1">
                  {classificationStats.classifiedPercentage}% of spend lines classified
                </Typography>
              </Box>
              {classificationAlert}
            </CardContent>
          </Card>
        </Grid>

        {/* Currency & Unit Standardization */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Standardization Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body1">
                  Currency Standardized: {standardizationStatus.currencyStandardized ? 'Yes' : 'No'}
                  <br />
                  Unit Standardized: {standardizationStatus.unitMeasureStandardized ? 'Yes' : 'No'}
                </Typography>
              </Box>
              {standardizationAlert}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataPrepPage; 